/**
 * Use this file for JavaScript code that you want to run in the front-end
 * on posts/pages that contain this block.
 */

import domReady from '@wordpress/dom-ready'; // Import the domReady function which executes a callback when the DOM is fully loaded
import React, {useState} from "react"; // Import for managing component state
import ReactDOM from "react-dom"; // Import for rendering React components to the DOM
import {__} from "@wordpress/i18n"; // Import i18n package for internationalization

// Wait for the DOM to be fully loaded before executing the callback function
domReady(function () {

	/**
	 * Function that renders the BMI calculator form, performs the BMI calculation, and displays the result.
	 */
	function BMICalculator() {
		// Get the selected height and weight units from hidden inputs
		const getHeightUnit = document.getElementById('heightUnit').value;
		const getWeightUnit = document.getElementById('weightUnit').value;

		// Constants for unit conversions
		const centimetersPerInch = 2.54; // 1 inch = 2.54 centimeters
		const kilogramsPerPound = 0.45359237; // 1 kg = 0.45359237 pounds

		// Declare state variables for form inputs and BMI result
		const [selectedGender, setSelectedGender] = useState('female'); // State for selected gender
		let [height, setHeight] = useState(''); // State for height input
		const [inchHeight, setInchHeight] = useState(''); // State for additional height in inches if using imperial units
		let [weight, setWeight] = useState(''); // State for weight input
		const [age, setAge] = useState(''); // State for age input
		const [index, setIndex] = useState(null); // State for calculated BMI index

		/**
		 * Function that calculates BMI based on height and weight inputs.
		 */
		const calculateBMI = (e) => {
			e.preventDefault(); // Prevent form submission

			// Convert height to meters
			if (getHeightUnit === 'inch') {
				height = height * 12; // Convert feet to inches
				height = Number(height) + Number(inchHeight); // Add additional inches
				height = height * centimetersPerInch / 100; // Convert inches to centimeters, then to meters
			} else {
				height = height / 100; // Convert centimeters to meters
			}

			// Convert weight to kilograms if necessary
			if (getWeightUnit === 'lbs') {
				weight = weight * kilogramsPerPound;
			}

			// Check that height and weight are greater than zero
			if (height <= 0 || weight <= 0) {
				return;
			}

			// Calculate BMI: weight divided by height squared
			const indexValue = weight / (height ** 2);
			setIndex(indexValue.toFixed(2)); // Set BMI index state with two decimal precision
		};

		// Return the JSX for the BMI calculator form and result display
		return (
			<>
				<div className={"form-bmi"}>
					<form id={"bmi-calculator-form"} onSubmit={calculateBMI}>
						<div className={"form-inner"}>
							{/* Gender selection input */}
							<div className={"form-input bmi-gender"}>
								<label htmlFor="selectedGender">{__("Votre genre", "superprof-gutenberg-blocks")}</label>
								<select
									name={"selectedGender"}
									value={selectedGender}
									onChange={e => setSelectedGender(e.target.value)}
								>
									<option value={"female"}>{__("Femme", "superprof-gutenberg-blocks")}</option>
									<option value={"male"}>{__("Homme", "superprof-gutenberg-blocks")}</option>
								</select>
							</div>
							{/* Height input */}
							<div className={"form-input bmi-height"}>
								<div className={"sub-input bmi-feet-height"}>
									<label htmlFor="height">{__("Votre taille", "superprof-gutenberg-blocks")}</label>
									<input
										type={"number"}
										id={"height"}
										name={"height"}
										placeholder={__("Votre taille", "superprof-gutenberg-blocks")}
										value={height}
										onChange={e => setHeight(e.target.value)}
										min={0}
										step={1}
										required
									/>
									<span className={"input-suffix"}>{(getHeightUnit === 'inch') ? __("pieds", "superprof-gutenberg-blocks") : __("cm", "superprof-gutenberg-blocks")}</span>
								</div>
								{/* Additional height input in inches if using imperial units */}
								{getHeightUnit === 'inch' &&
									<div className={"sub-input bmi-inch-height"}>
										<label htmlFor="inchHeight">{__("Votre taille", "superprof-gutenberg-blocks")}</label>
										<input
											type={"number"}
											id={"inchHeight"}
											name={"inchHeight"}
											placeholder={__("Votre taille", "superprof-gutenberg-blocks")}
											value={inchHeight}
											onChange={e => setInchHeight(e.target.value)}
											min={0}
											step={1}
											required
										/>
										<span className={"input-suffix"}>{__("pouces", "superprof-gutenberg-blocks")}</span>
									</div>
								}
							</div>
							{/* Weight input */}
							<div className={"form-input bmi-weight"}>
								<label htmlFor="weight">{__("Votre poids", "superprof-gutenberg-blocks")}</label>
								<input
									type={"number"}
									id={"weight"}
									name={"weight"}
									placeholder={__("Votre poids", "superprof-gutenberg-blocks")}
									value={weight}
									onChange={e => setWeight(e.target.value)}
									min={0}
									step={1}
									required
								/>
								<span className={"input-suffix"}>{(getWeightUnit === 'lbs') ? __("livres", "superprof-gutenberg-blocks") : __("kg", "superprof-gutenberg-blocks")}</span>
							</div>
							{/* Age input */}
							<div className={"form-input bmi-age"}>
								<label htmlFor="age">{__("Votre âge", "superprof-gutenberg-blocks")}</label>
								<input
									type={"number"}
									id={"age"}
									name={"age"}
									placeholder={__("Votre âge", "superprof-gutenberg-blocks")}
									value={age}
									onChange={e => setAge(e.target.value)}
									min={0}
									step={1}
									required
								/>
							</div>
							{/* Submit button */}
							<div className={"form-input bmi-submit"}>
								<input type={"submit"} value={__("Calculer l'IMC", "superprof-gutenberg-blocks")}/>
							</div>
						</div>
					</form>
				</div>
				{/* Display BMI result */}
				{index !== null && (
					<div className={"bmi-result"}>
						<div className={"bmi-index-result h3"}>{__("Résultat de votre IMC", "superprof-gutenberg-blocks")}<span>{index} kg/m²</span></div>
					</div>
				)}
			</>
		);
	}

	// Check if the DOM is ready and the bmiForm element exists, then render the form
	const bmiForm = document.getElementById("bmiForm");
	if (bmiForm) {
		ReactDOM.render(<BMICalculator/>, bmiForm); // Render the BMICalculator component into the bmiForm element
	}

});
